import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SaveIcon from "@mui/icons-material/Save";
import ConfirmDialog from "../../ConfirmDialog.jsx";
import { useAsyncDataGridPresets } from "./AsyncDataGridPresetsContext.js";

/**
 * Dialog for managing data grid presets (rename, default, delete, save new).
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 */
function AsyncDataGridPresetsConfigureDialog({ open, onClose }) {
  const presetsContext = useAsyncDataGridPresets();
  const [newName, setNewName] = React.useState("");
  const [renameTarget, setRenameTarget] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  if (!presetsContext) {
    return null;
  }

  const {
    gridKey,
    presets,
    activePresetId,
    getCurrentPresetState,
    createPreset,
    renamePreset,
    setDefaultPreset,
    deletePreset,
    clearActivePreset,
  } = presetsContext;

  const handleSaveNew = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      return;
    }
    setSaving(true);
    try {
      await createPreset({
        gridKey,
        name: trimmed,
        state: getCurrentPresetState(),
      });
      setNewName("");
    } finally {
      setSaving(false);
    }
  };

  const handleRenameSubmit = async () => {
    if (!renameTarget) {
      return;
    }
    const trimmed = renameValue.trim();
    if (!trimmed) {
      return;
    }
    await renamePreset({ _id: renameTarget._id, name: trimmed });
    setRenameTarget(null);
    setRenameValue("");
  };

  const handleSetDefault = async (preset) => {
    await setDefaultPreset({ _id: preset._id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }
    const wasActive = String(deleteTarget._id) === activePresetId;
    await deletePreset({ _id: deleteTarget._id });
    if (wasActive) {
      clearActivePreset();
    }
    setDeleteTarget(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Předvolby tabulky</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1, mb: 2, mt: 0.5 }}>
            <TextField
              label="Název nové předvolby"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              size="small"
              fullWidth
            />
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newName.trim() || saving}>
              Uložit
            </Button>
          </Box>

          {presets.length === 0 ? (
            <Typography color="text.secondary">Zatím nejsou uloženy žádné předvolby.</Typography>
          ) : (
            <List dense disablePadding>
              {presets.map((preset) => {
                const presetId = String(preset._id);
                const isActive = activePresetId === presetId;
                return (
                  <ListItem
                    key={presetId}
                    secondaryAction={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Tooltip title="Přejmenovat">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => {
                              setRenameTarget(preset);
                              setRenameValue(preset.name);
                            }}>
                            <DriveFileRenameOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={preset.isDefault ? "Zrušit výchozí předvolbu" : "Nastavit jako výchozí"}>
                          <IconButton edge="end" size="small" onClick={() => handleSetDefault(preset)}>
                            {preset.isDefault ? (
                              <StarIcon fontSize="small" color="primary" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Smazat">
                          <IconButton edge="end" size="small" onClick={() => setDeleteTarget(preset)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    sx={{
                      bgcolor: isActive ? "action.selected" : undefined,
                      borderRadius: 1,
                      mb: 0.5,
                    }}>
                    <ListItemText primary={preset.name} secondary={preset.isDefault ? "Výchozí" : undefined} />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Zavřít</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Přejmenovat předvolbu</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Název"
            fullWidth
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)}>Zrušit</Button>
          <Button variant="contained" onClick={handleRenameSubmit} disabled={!renameValue.trim()}>
            Uložit
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onSubmit={handleDeleteConfirm}
        title="Smazat předvolbu?"
        description={deleteTarget ? `Opravdu smazat předvolbu „${deleteTarget.name}"?` : ""}
        submitButton={{ label: "Smazat", color: "error" }}
      />
    </>
  );
}

AsyncDataGridPresetsConfigureDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AsyncDataGridPresetsConfigureDialog;
